import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { getMessageType, getTenantMessageCount } from "../../store/actions/partnerAction";
import { getTenantWaba } from "../../store/actions/tenantAction";
import { getTenants } from "../../store/actions/partnerAction";
import TypeableSelect from "../../components/Dropdowns/TypeableSelect";
import { useTranslation } from "react-i18next";
const { RangePicker } = DatePicker;

function WabaUtilization({ showSidebar }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [usageData, setUsageData] = useState([]);
    const [companyList, setCompanyList] = useState([]);
    const [wabas, setWabas] = useState([]);
    const [selectedTenantId, setSelectedTenantId] = useState(null);
    const [selectedWabaId, setSelectedWabaId] = useState(null);
    const [messageTypes, setMessageTypes] = useState([]);
    const [messageType, setMessageType] = useState();
    const [dateRange, setDateRange] = useState({
        fromDate: dayjs().subtract(1, "month").format("YYYY-MM-DD"),
        toDate: dayjs().format("YYYY-MM-DD"),
    });
    const partnerReducer = useSelector((state) => state.partnerReducer);
    const partnerTimezone =
        partnerReducer.partnerDetails?.timezone ??
        Intl.DateTimeFormat().resolvedOptions().timeZone;

    useEffect(() => {
        dispatch(
            getTenants(
                {
                    select: ["distinct t.id,t.name"],
                    criteria: { active: true },
                    count: true,
                    size: 500,
                    offset: 0,
                },
                (d) => {
                    setCompanyList(d?.data ?? []);
                }
            )
        );
        dispatch(getMessageType(setMessageTypes));
        onSearch();
    }, []);

    useEffect(() => {
        if (usageData && usageData.length > 0) onSearch();
        if (selectedTenantId) {
            dispatch(
                getTenantWaba(
                    { tenantId: selectedTenantId, criteria: { active: true } },
                    (d) => {
                        setWabas(d?.data ?? []);
                        setSelectedWabaId(null);
                    }
                )
            );
        } else {
            setWabas([]);
            setSelectedWabaId(null);
        }
    }, [selectedTenantId]);

    useEffect(() => {
        if (selectedWabaId) onSearch();
    }, [selectedWabaId]);

    const buildRequest = () => {
        const req = {};
        if (selectedTenantId) req.tenantId = selectedTenantId;
        if (selectedWabaId) req.tenantWabaId = selectedWabaId;
        if (dateRange.fromDate) req.fromDate = dateRange.fromDate;
        if (dateRange.toDate) req.toDate = dateRange.toDate;
        if (messageType) req.messageType = messageType;
        return req;
    };

    const onSearch = () => {
        dispatch(
            getTenantMessageCount(buildRequest(), (d) => {
                setUsageData(d ?? []);
            })
        );
    };

    const exportReport = () => {
        const data = usageData.map((item) => ({
            "Company Name": item.tenantName ?? item.tenantId ?? "-",
            "WABA": item.wabaName ? `${item.wabaName}${item.phone ? ` +${item.phoneCode} ${item.phone}` : ""}` : "-",
            "Total": item.totalScheduled ?? 0,
            "Sent": item.sent ?? 0,
            // "Accepted": item.accepted ?? 0,
            "Delivered": item.delivered ?? 0,
            "Read": item.read ?? 0,
            "Failed": item.failed ?? 0,
            "Queued": item.queued ?? 0,
        }));
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(data);
        XLSX.utils.book_append_sheet(workbook, worksheet, "company_utilization");
        XLSX.writeFile(workbook, "company_utilization.xlsx");
    };

    const selectDateRange = (dates, dateStrings) => {
        if (dates && dates.length === 2 && dates[0] && dates[1]) {
            setDateRange({ fromDate: dateStrings[0], toDate: dateStrings[1] });
        } else {
            setDateRange({ "fromDate": "2024-01-01", "toDate": new Date().toISOString().split("T")[0] });
        }
    };

    const statCell = (value) => (
        <td className="py-3 text-[13px] font-normal text-center">
            {new Intl.NumberFormat("en-US").format(value ?? 0)}
        </td>
    );

    return (
        <div className="h-screen">
            <div
                className={`fixed top-[30px] ${showSidebar ? "fullWidth" : "fullWidth1"
                    } transition-all duration-200 ease-out z-30 bg-white mb-[0px]`}
            >
                <div className="flex gap-5 justify-between px-5 py-[8.5px] min-h-[53.8px] text-base text-center bg-white border-b-[1px] border-[#e9ebf0] border-solid max-md:flex-wrap">
                    <div className="flex gap-2 items-center my-auto font-medium text-black whitespace-nowrap cursor-pointer">
                        <i className="fa-solid fa-chart-bar text-[14px] text-theme"></i>
                        <div className="text-[14px] font-medium">{t("Waba Utilization")}</div>
                    </div>
                    <div className="flex gap-2 items-center">
                        <button
                            type="button"
                            onClick={exportReport}
                            className="min-w-[76px] text-center px-[16px] py-[6px] text-white font-medium text-[14px] bg-theme transition-all ease-in-out rounded cursor-pointer hover:bg-primary"
                        >
                            {t("Download Excel")}
                        </button>
                    </div>
                </div>
                <div className="bg-white text-black font-medium px-5 pt-[6px] pb-[10px] box-border border-b">
                    <div className="grid grid-cols-12 gap-4 items-end">
                        <div className="flex flex-col col-span-3">
                            <div className="text-[#656F7D] text-[13px] font-normal mb-1">
                                <label>{t("Company")}</label>
                            </div>
                            <TypeableSelect
                                list={companyList}
                                selected={companyList.find((c) => c.id === selectedTenantId) ?? {}}
                                labelField="name"
                                valueField="id"
                                placeholder={t("All Companies")}
                                onSelect={(s) => {
                                    setSelectedTenantId(s?.id ?? null);
                                }}
                            />
                        </div>
                        <div className="flex flex-col col-span-2">
                            <div className="text-[#656F7D] text-[13px] font-normal mb-1">
                                <label>{t("WABA")}</label>
                            </div>
                            <select
                                className="border border-gray-300 rounded py-[8px] px-[12px] focus:outline-none focus:ring-[0.5px] focus:ring-theme text-[14px] text-black"
                                value={selectedWabaId ?? ""}
                                onChange={(e) => {
                                    setSelectedWabaId(e.target.value ? parseInt(e.target.value) : null);
                                }}
                                disabled={!selectedTenantId}
                            >
                                <option value="">{t("All WABAs")}</option>
                                {wabas.map((w, i) => (
                                    <option value={w.id} key={i}>
                                        {`${w?.teamName ?? ""}  +${w?.phoneCode} ${w?.phone}`}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="flex flex-col col-span-2">
                            <div className="text-[#656F7D] text-[13px] font-normal mb-1">
                                <label>{t("Message Types")}</label>
                            </div>
                            <select
                                className="border border-gray-300 rounded py-[8px] px-[12px] focus:outline-none focus:ring-[0.5px] focus:ring-theme text-[14px] text-black"
                                value={messageType}
                                onChange={(e) => {
                                    setMessageType(e.target.value);
                                }}>
                                <option value="">{t("All Message Types")}</option>
                                {messageTypes.map((w, i) => (
                                    <option value={w.messageType} key={i}>
                                        {w?.messageType ?? ""}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="flex flex-col col-span-3">
                            <div className="text-[#656F7D] text-[13px] font-normal mb-1">
                                <label>{t("Date Range")}</label>
                            </div>
                            <RangePicker
                                className="h-10"
                                onChange={selectDateRange}
                                format="YYYY-MM-DD"
                                defaultValue={[dayjs().subtract(1, "month"), dayjs()]}
                            />
                        </div>
                        <div className="flex flex-col">
                            <button
                                type="button"
                                className="justify-center items-center flex px-2 h-10 py-1 font-semibold text-[14px] text-white bg-theme rounded cursor-pointer"
                                onClick={onSearch}
                            >
                                {t("Search")}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex flex-col h-full justify-between grow">
                <div className="px-5 pt-5 mt-[9rem] mb-[20px] overflow-x-auto">
                    {usageData && usageData.length > 0 ? (
                        <table className="text-sm text-left mt-1 rtl:text-right text-midBlack w-full min-w-[1100px]">
                            <thead className="text-[13px] text-midBlack bg-gray-200">
                                <tr>
                                    <th scope="col" className="pb-1 font-[500] pr-2 pl-2 py-3">
                                        {t("Sr. No.")}
                                    </th>
                                    <th scope="col" className="font-[500] py-3 pl-2 min-w-[200px]">
                                        {t("Company Name")}
                                    </th>
                                    <th scope="col" className="font-[500] min-w-[160px]">
                                        {t("WABA")}
                                    </th>
                                    <th scope="col" className="font-[500] text-center px-9">
                                        {t("Total")}
                                    </th>
                                    <th scope="col" className="font-[500] text-center px-5">
                                        {t("Sent")}
                                    </th>
                                    {/* <th scope="col" className="font-[500] text-center px-2">
                                        {t("Accepted")}
                                    </th> */}
                                    <th scope="col" className="font-[500] text-center px-5">
                                        {t("Delivered")}
                                    </th>
                                    <th scope="col" className="font-[500] text-center px-5">
                                        {t("Read")}
                                    </th>
                                    <th scope="col" className="font-[500] text-center px-5">
                                        {t("Failed")}
                                    </th>
                                    <th scope="col" className="font-[500] text-center px-2">
                                        {t("Queued")}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {usageData.map((item, i) => (
                                    <tr className="bg-white border-b" key={i}>
                                        <td className="py-3 text-[13px] pl-2">{i + 1}</td>
                                        <td className="py-3 text-[13px] pl-2 font-normal">
                                            {item.tenantName ?? item.tenantId ?? "-"}
                                        </td>
                                        <td className="py-3 text-[13px] font-normal">
                                            {item.wabaName
                                                ? `${item.wabaName}${item.phone ? ` +${item.phoneCode} ${item.phone}` : ""}`
                                                : "-"}
                                        </td>
                                        {statCell(item.totalScheduled)}
                                        {statCell(item.sent)}
                                        {/* {statCell(item.accepted)} */}
                                        {statCell(item.delivered)}
                                        {statCell(item.read)}
                                        <td className="py-3 text-[13px] font-normal text-center">
                                            <span className={item.failed > 0 ? "text-red-500 font-medium" : ""}>
                                                {new Intl.NumberFormat("en-US").format(item.failed ?? 0)}
                                            </span>
                                        </td>
                                        {statCell(item.queued)}
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="bg-gray-100 text-[13px] font-semibold">
                                <tr>
                                    <td className="py-3 pl-2" colSpan={3}>{t("Total")}</td>
                                    <td className="py-3 text-center">
                                        {new Intl.NumberFormat("en-US").format(usageData.reduce((s, r) => s + (r.totalScheduled ?? 0), 0))}
                                    </td>
                                    <td className="py-3 text-center">
                                        {new Intl.NumberFormat("en-US").format(usageData.reduce((s, r) => s + (r.sent ?? 0), 0))}
                                    </td>
                                    {/* <td className="py-3 text-center">
                                        {new Intl.NumberFormat("en-US").format(usageData.reduce((s, r) => s + (r.accepted ?? 0), 0))}
                                    </td> */}
                                    <td className="py-3 text-center">
                                        {new Intl.NumberFormat("en-US").format(usageData.reduce((s, r) => s + (r.delivered ?? 0), 0))}
                                    </td>
                                    <td className="py-3 text-center">
                                        {new Intl.NumberFormat("en-US").format(usageData.reduce((s, r) => s + (r.read ?? 0), 0))}
                                    </td>
                                    <td className="py-3 text-center text-red-500">
                                        {new Intl.NumberFormat("en-US").format(usageData.reduce((s, r) => s + (r.failed ?? 0), 0))}
                                    </td>
                                    <td className="py-3 text-center">
                                        {new Intl.NumberFormat("en-US").format(usageData.reduce((s, r) => s + (r.queued ?? 0), 0))}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    ) : (
                        <div className="flex justify-center items-center h-[300px] text-gray-400 text-[14px]">
                            {t("No records found")}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default WabaUtilization;
